import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";
import LandingPage from "@/pages/LandingPage";
import PlatformPage from "@/pages/PlatformPage";
import WhatsNewPage from "@/pages/WhatsNewPage";
import CapabilitiesPage from "@/pages/CapabilitiesPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import SignUpPage from "@/pages/SignUpPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import ScannerPage from "@/pages/ScannerPage";
import ScreenerPage from "@/pages/ScreenerPage";
import PortfolioPage from "@/pages/PortfolioPage";
import TickerWorkbench from "@/pages/TickerWorkbench";
import TickerIntelligencePage from "@/pages/TickerIntelligencePage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import ReliabilityLab from "@/pages/ReliabilityLab";
import BacktestLab from "@/pages/BacktestLab";
import TradeLogPage from "@/pages/TradeLogPage";
import HedgingPage from "@/pages/HedgingPage";
import HowToPage from "@/pages/HowToPage";
import BillingPage from "@/pages/BillingPage";
import ProfilePage from "@/pages/ProfilePage";
import LegalPage from "@/pages/LegalPage";
import UpgradeModal from "@/components/UpgradeModal";
import TrialGate from "@/components/TrialGate";
import HaircutAnalyticsPage from "@/pages/HaircutAnalyticsPage";
import WheelIncomePage from "@/pages/WheelIncomePage";
import PositionManagementPage from "@/pages/PositionManagementPage";
import ShadowMoneyPage from "@/pages/ShadowMoneyPage";
import TickerTalkPage from "@/pages/TickerTalkPage";
// Iter158 — Workspace App (see /app/workspace-app/README/EXTRACTION.md).
// Distinct product, namespaced under /workspace-app/*.
import WorkspacePendingApprovalPage from "@/pages/workspace/PendingApprovalPage";
import WorkspaceAdminPendingPage from "@/pages/workspace/AdminPendingPage";
import WorkspaceAdminUsersPage from "@/pages/workspace/AdminUsersPage";
import WorkspaceLegalPage from "@/pages/workspace/LegalPage";
import { ViewAsProvider, ViewAsBanner } from "@/contexts/ViewAsContext";
// Iter159 — MarkovLab paused-user takeover.
import TrialEndedPage from "@/pages/TrialEndedPage";
// Iter160 — broker/data setup extracted from HowToPage.
import SetupPage from "@/pages/SetupPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ContactPage from "@/pages/ContactPage";
import WheelRowPreviewPage from "@/pages/WheelRowPreviewPage";
import CockpitPreviewPage from "@/pages/CockpitPreviewPage";
import TickerWorkspacePreviewPage from "@/pages/TickerWorkspacePreviewPage";
import MacroMarketsPreviewPage from "@/pages/MacroMarketsPreviewPage";
import ScannerRefreshPreviewPage from "@/pages/ScannerRefreshPreviewPage";
import PlanA1OptionsFixturePage from "@/pages/PlanA1OptionsFixturePage";

function SessionHandoffError() {
  const { sessionError, checkAuth, logout } = useAuth();
  return (
    <div className="flex items-center justify-center h-screen px-6" style={{ background: "var(--hoip-bg)" }}>
      <div className="max-w-md text-center space-y-3">
        <div className="text-sm" style={{ color: "var(--hoip-text-secondary)" }}>
          {sessionError || "We signed you in but could not open your MarkovLab account."}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => checkAuth()}
            className="px-3 py-1.5 text-xs font-bold rounded-sm"
            style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
          >
            Retry
          </button>
          <button
            type="button"
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            className="px-3 py-1.5 text-xs rounded-sm border"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, clerkSignedIn } = useAuth();
  // Keep the current page mounted while a silent session refresh runs so
  // Wheel/Scanner state does not bleed into a dashboard remount.
  if (loading && user) return children;
  if (loading) return (
    <div className="flex items-center justify-center h-screen" style={{ background: "var(--hoip-bg)" }}>
      <div className="text-sm" style={{ color: "var(--hoip-text-secondary)" }}>Signing you in…</div>
    </div>
  );
  if (clerkSignedIn && !user) return <SessionHandoffError />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/whats-new" element={<WhatsNewPage />} />
      <Route path="/capabilities" element={<CapabilitiesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/login/*" element={<LoginPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/waitlist" element={<Navigate to="/sign-up" replace />} />
      {/* Public legal pages — reachable from the footer without login. */}
      <Route path="/legal/terms" element={<LegalPage />} />
      <Route path="/legal/risk" element={<LegalPage />} />
      <Route path="/legal/risk-disclosure" element={<Navigate to="/legal/risk" replace />} />
      <Route path="/legal/privacy" element={<LegalPage />} />
      <Route path="/legal/refund" element={<LegalPage />} />
      {/* Iter168 — stable auth-callback route so the pending screen persists
          after `history.replaceState` strips the OAuth fragment. Without
          this, AppRouter would fall through to the "*" catch-all and blow
          the pending screen away 1-2 seconds after render. */}
      <Route path="/auth-callback" element={<AuthCallback />} />
      {/* Iter158 — Workspace App (New Project) — namespaced under
          /workspace-app/*. Standalone auth/approval flow separate from
          MarkovLab. See /app/workspace-app/README/EXTRACTION.md. */}
      <Route path="/workspace-app/pending-approval" element={<WorkspacePendingApprovalPage />} />
      <Route path="/workspace-app/legal/:kind" element={<WorkspaceLegalPage />} />
      {/* Iter159 — MarkovLab paused-user takeover (public route so the 402
          redirect works even when tokens have expired). */}
      <Route path="/trial-ended" element={<TrialEndedPage />} />
      {/* Iter160 — broker/data setup (extracted from How-To). */}
      <Route path="/setup" element={<SetupPage />} />
      {process.env.NODE_ENV !== "production" ? (
        <>
          <Route path="/dev/wheel-row-preview" element={<WheelRowPreviewPage />} />
          <Route path="/dev/cockpit-preview" element={<CockpitPreviewPage />} />
          <Route path="/dev/ticker-workspace-preview" element={<TickerWorkspacePreviewPage />} />
          <Route path="/dev/macro-markets-preview" element={<MacroMarketsPreviewPage />} />
          <Route path="/dev/scanner-refresh-preview" element={<ScannerRefreshPreviewPage />} />
          <Route path="/dev/plan-a1-options-fixture" element={<PlanA1OptionsFixturePage />} />
        </>
      ) : null}
      <Route path="/workspace-app/admin/pending" element={
        <ProtectedRoute><WorkspaceAdminPendingPage /></ProtectedRoute>
      } />
      <Route path="/workspace-app/admin/users" element={
        <ProtectedRoute><WorkspaceAdminUsersPage /></ProtectedRoute>
      } />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        {/* Iter F (iter137): new canonical route + old alias for compat */}
        <Route path="wheel-strategies" element={<WheelIncomePage />} />
        <Route path="position-management" element={<PositionManagementPage />} />
        <Route path="wheel-income" element={<Navigate to="/wheel-strategies" replace />} />
        <Route path="scanner" element={<ScannerPage />} />
        <Route path="shadow-money" element={<ShadowMoneyPage />} />
        <Route path="ticker-talk" element={<TickerTalkPage />} />
        <Route path="screener" element={
          <TrialGate featureName="Model Screener"
                     featureBlurb="Rank the full sector universe by regime-adjusted score, HMM confidence, expected sojourn and IV context.">
            <ScreenerPage />
          </TrialGate>
        } />
        <Route path="portfolio" element={<PortfolioPage />} />
        <Route path="trade-log" element={<TradeLogPage />} />
        <Route path="hedging" element={
          <TrialGate featureName="Hedging Lab"
                     featureBlurb="Portfolio-level dispersion, tail-risk hedge sizing, and macro overlay analytics.">
            <HedgingPage />
          </TrialGate>
        } />
        <Route path="how-to" element={<HowToPage />} />
        <Route path="ticker/:symbol/intelligence" element={<TickerIntelligencePage />} />
        <Route path="ticker/:symbol" element={<TickerWorkbench />} />
        <Route path="reliability" element={
          <TrialGate featureName="Reliability Lab"
                     featureBlurb="SORM + Bayesian pre-trade screening for each ranked strategy — quantifies drawdown risk before you commit capital.">
            <ReliabilityLab />
          </TrialGate>
        } />
        {/* Admin-only telemetry — deliberately NOT linked from the primary
            nav. Access via direct URL /admin/haircut-analytics. */}
        <Route path="admin/haircut-analytics" element={<HaircutAnalyticsPage />} />
        <Route path="backtest" element={<BacktestLab />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route
          path="admin"
          element={<AdminRoute><AdminPage /></AdminRoute>}
        />
      </Route>
      {/* FIX-02 — real 404 page; never redirect unknown routes to / or /login. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <AuthProvider>
          <ViewAsProvider>
            <ViewAsBanner />
            <AppRouter />
            <UpgradeModal />
          </ViewAsProvider>
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
